<template>
  <div class="mt-2">
    <!-- Generate signature boxes for attendees -->
    <div
      v-if="attendees && attendees.length > 0"
      class="space-y-4 mt-4"
    >
      <div
        v-for="attendee in attendees"
        :key="attendee.id"
        class="w-full border rounded p-2 relative shadow-xs"
        style="height: auto"
      >
        <div class="flex items-center justify-between pb-2">
          <div class="flex items-center">
            <p class="pl-3">
              <span class="font-semibold text-gray-800">
                {{ attendee.attendeeName }}
                <span
                  v-if="attendee.attendeeType"
                  class="text-sm font-semibold text-gray-500 ml-2"
                >
                  {{ attendee.attendeeType }}
                </span>
              </span>
            </p>
          </div>
        </div>

        <!-- Signature area - always show both existing signature and add new option -->
        <div class="flex h-full gap-4">
          <!-- Original signature from attendee data (immutable) -->
          <div class="flex-1 flex flex-col">
            <div class="text-sm font-medium text-gray-600 mb-2">
              Original Signature:
            </div>
            <div
              v-if="Helper.hasSignature(attendee)"
              class="flex-1 border rounded bg-white flex items-center justify-center"
            >
              <SignatureImg
                :signature="attendee"
                class="object-contain max-h-[80px] w-full"
              />
            </div>
            <div
              v-else
              class="flex-1 border rounded bg-gray-50 flex items-center justify-center text-gray-400 text-sm"
            >
              No original signature
            </div>
          </div>

          <!-- New signature input -->
          <div class="flex-1 flex flex-col">
            <div class="text-sm font-medium text-gray-600 mb-2">
              New Signature:
            </div>
            <div
              v-if="getSignatureForAttendee(attendee.id)"
              class="flex-1 border rounded bg-white flex items-center justify-center relative"
            >
              <img
                :src="getSignatureForAttendee(attendee.id).signatureData"
                class="object-contain max-h-[80px] w-full"
              />
              <!-- N/A indicator -->
              <div
                v-if="getSignatureForAttendee(attendee.id).isNotAvailable"
                class="absolute top-1 left-1 bg-red-100 text-red-800 text-xs px-2 py-1 rounded border border-red-200"
              >
                N/A
              </div>
              <!-- Remove button for new signature -->
              <Button
                icon="pi pi-times"
                class="absolute top-1 right-1 p-button-text p-button-sm text-red-500"
                :disabled="disabled"
                @click="
                  () => {
                    if (!disabled) {
                      removeSignatureForAttendee(attendee.id);
                    }
                  }
                "
                aria-label="Remove"
              />
            </div>
            <div
              v-else
              class="flex-1 border-2 border-dashed rounded flex flex-col justify-center items-center p-4"
              :style="{
                borderColor: invalid ? '#ef4444' : '#e5e7eb',
                backgroundColor: invalid ? '#fef2f2' : '#f9fafb'
              }"
            >
              <div class="text-sm font-medium text-gray-600 mb-4">
                Select Option:
              </div>
              <div class="flex gap-3 w-full">
                <Button
                  label="Sign"
                  icon="pi pi-pencil"
                  class="flex-1"
                  :disabled="disabled"
                  @click="
                    () => {
                      if (!disabled) {
                        openAttendeeSignatureDialog(attendee);
                      }
                    }
                  "
                />
                <Button
                  label="N/A"
                  icon="pi pi-times"
                  severity="secondary"
                  class="flex-1"
                  :disabled="disabled"
                  @click="
                    () => {
                      if (!disabled) {
                        setAttendeeNotAvailable(attendee);
                      }
                    }
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No attendees message -->
    <div
      v-if="!attendees || attendees.length === 0"
      class="text-center text-gray-500 py-4"
    >
      No attendees found. Please ensure the attendee data is available.
    </div>

    <!-- Attendee Signature Dialog -->
    <SignatureDialog
      v-if="attendeeSignatureObj.isVisible"
      v-model:visible="attendeeSignatureObj.isVisible"
      :skipAttendee="true"
      :attendeeName="currentAttendeeName"
      @onConfirm="attendeeSignatureObj.onConfirm"
    />
  </div>
</template>

<script>
  import { ref, defineComponent, inject } from "vue";
  import { useRoute } from "vue-router";
  import SignatureDialog from "@/components/SignatureDialog.vue";
  import SignatureImg from "@/components/SignatureImg.vue";
  import Helper from "@/utils/helper";
  import Constants from "@/utils/constants";
  import { useSignaturesStore } from "@/stores/signaturesStore";

  export default defineComponent({
    name: "AttendeeSignatureField",
    components: {
      SignatureDialog,
      SignatureImg
    },
    props: {
      attendees: {
        type: Array,
        default: () => []
      },
      disabled: {
        type: Boolean,
        default: false
      },
      invalid: {
        type: Boolean,
        default: false
      },
      // For FormInputComponent compatibility
      response: {
        type: Object,
        default: () => ({})
      },
      attendeeDataKey: {
        type: String,
        default: ""
      },
      updateStore: {
        type: Function,
        default: () => {}
      }
    },
    emits: ["update:signatures"],
    setup(props, { emit }) {
      const route = useRoute();
      const signaturesStore = useSignaturesStore();
      const signaturesNetworkFallback = inject(
        "signaturesNetworkFallback",
        false
      );
      const resolvedSignatures = ref({});
      const currentAttendeeName = ref("");

      // Create unified data structure for both dynamic and static forms
      const response = props.response || { attendees: props.attendees };
      const attendeeDataKey = props.attendeeDataKey || "attendees";
      const updateStore =
        props.updateStore || ((data) => emit("update:signatures", data));

      // Attendee signature dialog
      const attendeeSignatureObj = ref({
        isVisible: false,
        currentAttendee: null,
        onConfirm: (val) => onAttendeeSignatureConfirm(val)
      });

      const onAttendeeSignatureConfirm = async (val) => {
        if (!attendeeSignatureObj.value.currentAttendee) return;

        const { id, signatureData, signatureDate } = val;
        const currentAttendee = attendeeSignatureObj.value.currentAttendee;

        try {
          await signaturesStore.insertSignature({
            id,
            table_name:
              Constants.enums.TABLES_NAME_ENUM.FORM_TEMPLATES_RESPONSES_TABLE,
            record_id: route.params.res || null,
            name: currentAttendee.attendeeName,
            data: signatureData,
            attendeeType: currentAttendee.attendeeType,
            employeeRefID: currentAttendee.employeeRefID || null,
            by: Helper.getCurrentUserId()
          });

          const signOffData = {
            id,
            signatureDate,
            isSignatureRef: true,
            isNotAvailable: false
          };

          const attendeeData = Helper.findAndSetKeyInObject(
            response,
            attendeeDataKey
          );

          if (Array.isArray(attendeeData)) {
            const attendeeIndex = attendeeData.findIndex(
              (a) => a.id === currentAttendee.id
            );

            if (attendeeIndex >= 0) {
              const updatedAttendeeData = [...attendeeData];
              updatedAttendeeData[attendeeIndex] = {
                ...updatedAttendeeData[attendeeIndex],
                signOffData
              };

              Helper.findAndSetKeyInObject(
                response,
                attendeeDataKey,
                updatedAttendeeData
              );

              updateStore(
                Helper.findAndSetKeyInObject(response, attendeeDataKey)
              );
            }
          }
        } catch (error) {
          // Fallback: store full signatureData so signature is not lost
          const signOffData = {
            signatureData: val.signatureData,
            signatureDate: val.signatureDate,
            isNotAvailable: false
          };

          const attendeeData = Helper.findAndSetKeyInObject(
            response,
            attendeeDataKey
          );

          if (Array.isArray(attendeeData)) {
            const attendeeIndex = attendeeData.findIndex(
              (a) => a.id === currentAttendee.id
            );

            if (attendeeIndex >= 0) {
              const updatedAttendeeData = [...attendeeData];
              updatedAttendeeData[attendeeIndex] = {
                ...updatedAttendeeData[attendeeIndex],
                signOffData
              };

              Helper.findAndSetKeyInObject(
                response,
                attendeeDataKey,
                updatedAttendeeData
              );

              updateStore(
                Helper.findAndSetKeyInObject(response, attendeeDataKey)
              );
            }
          }
        }

        attendeeSignatureObj.value.currentAttendee = null;
      };

      const resolveSignatureForAttendee = async (attendeeId, signatureId) => {
        if (resolvedSignatures.value[attendeeId]) return;
        try {
          const result = await signaturesStore.getSignatureById(signatureId, {
            networkFallback: signaturesNetworkFallback
          });
          if (result?.data?.data) {
            resolvedSignatures.value = {
              ...resolvedSignatures.value,
              [attendeeId]: result.data.data
            };
          }
        } catch {
          // silently skip — no image shown for this attendee
        }
      };

      const getSignatureForAttendee = (attendeeId) => {
        const attendeeData = Helper.findAndSetKeyInObject(
          response,
          attendeeDataKey
        );

        if (!Array.isArray(attendeeData)) return null;

        const attendee = attendeeData.find((a) => a.id === attendeeId);
        if (!attendee?.signOffData) return null;

        const signOff = attendee.signOffData;

        // Old records: inline signatureData, use directly
        if (signOff.signatureData) return signOff;

        // New records: resolve ref lazily
        if (signOff.isSignatureRef && signOff.id) {
          const cached = resolvedSignatures.value[attendeeId];
          if (cached) {
            return { ...signOff, signatureData: cached };
          }
          // Fire-and-forget — Vue reactivity will trigger a re-render when resolved
          resolveSignatureForAttendee(attendeeId, signOff.id);
          return null;
        }

        return null;
      };

      const removeSignatureForAttendee = async (attendeeId) => {
        // Always use FormInputComponent logic
        // Get the attendee data array
        const attendeeData = Helper.findAndSetKeyInObject(
          response,
          attendeeDataKey
        );

        if (Array.isArray(attendeeData)) {
          // Find the attendee in the array
          const attendeeIndex = attendeeData.findIndex(
            (attendeeItem) => attendeeItem.id === attendeeId
          );

          if (attendeeIndex >= 0) {
            // Soft-delete from signatures table if a row exists for this id;
            // no-ops for old inline records that never wrote to the table.
            const sigId = attendeeData[attendeeIndex]?.signOffData?.id;
            if (sigId) {
              try {
                await signaturesStore.softDeleteSignature(sigId, {
                  networkFallback: signaturesNetworkFallback
                });
              } catch (e) {
                console.warn(
                  "removeSignatureForAttendee: soft-delete failed",
                  e
                );
              }
            }

            // Drop any locally-cached resolved blob for this attendee
            if (resolvedSignatures.value[attendeeId]) {
              const next = { ...resolvedSignatures.value };
              delete next[attendeeId];
              resolvedSignatures.value = next;
            }

            // Create a new array to avoid circular references
            const updatedAttendeeData = [...attendeeData];

            // Remove the signOffData from the attendee
            const updatedAttendee = { ...updatedAttendeeData[attendeeIndex] };
            delete updatedAttendee.signOffData;

            updatedAttendeeData[attendeeIndex] = updatedAttendee;

            // Update the response object
            Helper.findAndSetKeyInObject(
              response,
              attendeeDataKey,
              updatedAttendeeData
            );

            // Update the store with a deep copy to avoid circular references
            updateStore(
              Helper.findAndSetKeyInObject(response, attendeeDataKey)
            );
          }
        }
      };

      const openAttendeeSignatureDialog = (attendee) => {
        currentAttendeeName.value = attendee.attendeeName;
        attendeeSignatureObj.value.currentAttendee = attendee;
        attendeeSignatureObj.value.isVisible = true;
      };

      const setAttendeeNotAvailable = async (attendee) => {
        const emptySvgData =
          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ3aGl0ZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9ImdyYXkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5OL0E8L3RleHQ+PC9zdmc+";

        const signatureId = Helper.getRandomUuid();
        const signatureDate = new Date().toISOString();

        try {
          await signaturesStore.insertSignature({
            id: signatureId,
            table_name:
              Constants.enums.TABLES_NAME_ENUM.FORM_TEMPLATES_RESPONSES_TABLE,
            record_id: route.params.res || null,
            name: attendee.attendeeName,
            data: emptySvgData,
            attendeeType: attendee.attendeeType,
            employeeRefID: attendee.employeeRefID || null,
            by: Helper.getCurrentUserId()
          });

          const signOffData = {
            id: signatureId,
            signatureDate,
            isSignatureRef: true,
            isNotAvailable: true
          };

          const attendeeData = Helper.findAndSetKeyInObject(
            response,
            attendeeDataKey
          );

          if (Array.isArray(attendeeData)) {
            const attendeeIndex = attendeeData.findIndex(
              (a) => a.id === attendee.id
            );

            if (attendeeIndex >= 0) {
              const updatedAttendeeData = [...attendeeData];
              updatedAttendeeData[attendeeIndex] = {
                ...updatedAttendeeData[attendeeIndex],
                signOffData
              };

              Helper.findAndSetKeyInObject(
                response,
                attendeeDataKey,
                updatedAttendeeData
              );

              updateStore(
                Helper.findAndSetKeyInObject(response, attendeeDataKey)
              );
            }
          }
        } catch (error) {
          // Fallback: store empty SVG inline
          const signOffData = {
            signatureData: emptySvgData,
            signatureDate,
            isNotAvailable: true
          };

          const attendeeData = Helper.findAndSetKeyInObject(
            response,
            attendeeDataKey
          );

          if (Array.isArray(attendeeData)) {
            const attendeeIndex = attendeeData.findIndex(
              (a) => a.id === attendee.id
            );

            if (attendeeIndex >= 0) {
              const updatedAttendeeData = [...attendeeData];
              updatedAttendeeData[attendeeIndex] = {
                ...updatedAttendeeData[attendeeIndex],
                signOffData
              };

              Helper.findAndSetKeyInObject(
                response,
                attendeeDataKey,
                updatedAttendeeData
              );

              updateStore(
                Helper.findAndSetKeyInObject(response, attendeeDataKey)
              );
            }
          }
        }
      };

      return {
        attendeeSignatureObj,
        currentAttendeeName,
        getSignatureForAttendee,
        removeSignatureForAttendee,
        openAttendeeSignatureDialog,
        setAttendeeNotAvailable,
        resolvedSignatures,
        Helper
      };
    }
  });
</script>
